/**
 * version.js — single source of truth for the running frontend build.
 *
 * Resolves three pieces of identity, in priority order:
 *   - semver        from REACT_APP_VERSION       (e.g. "0.1.0-beta")
 *   - sha           from REACT_APP_GIT_SHA       (auto-injected at build time
 *                   via the `build` script in package.json, which reads
 *                   $VERCEL_GIT_COMMIT_SHA on Vercel)
 *   - buildDate     from REACT_APP_BUILD_DATE    (also injected at build time)
 *
 * The combined `getAppVersion()` string is what we attach to Sentry,
 * feedback payloads, and the Settings footer. Format:
 *
 *     <semver>+<sha7>.<YYYY-MM-DD>
 *     e.g. "0.1.0-beta+a3f9c12.2026-04-27"
 *
 * Falls back to "0.0.0-dev" when nothing is wired (local dev).
 */

const RAW_SEMVER = process.env.REACT_APP_VERSION || "";
const RAW_SHA = process.env.REACT_APP_GIT_SHA || "";
const RAW_BUILD_DATE = process.env.REACT_APP_BUILD_DATE || "";

// 7-char short SHA matches `git rev-parse --short` and is the format every
// Git host (GitHub, Vercel UI, Railway UI) shows by default.
const shortSha = RAW_SHA ? RAW_SHA.slice(0, 7) : "";

// Keep just the date (YYYY-MM-DD) — time-of-day is noise in a footer.
const buildDateOnly = RAW_BUILD_DATE ? RAW_BUILD_DATE.slice(0, 10) : "";

/**
 * Combined version string for tagging events / errors / feedback.
 * Always returns *something* so Sentry filters never silently drop events.
 */
export const getAppVersion = () => {
  if (!RAW_SEMVER && !shortSha) return "0.0.0-dev";

  const base = RAW_SEMVER || "0.0.0";
  const suffixParts = [];
  if (shortSha) suffixParts.push(shortSha);
  if (buildDateOnly) suffixParts.push(buildDateOnly);
  return suffixParts.length ? `${base}+${suffixParts.join(".")}` : base;
};

/**
 * Structured access — used by the Settings footer so we can format each
 * field independently.
 */
export const getVersionParts = () => ({
  semver: RAW_SEMVER || "0.0.0-dev",
  sha: shortSha,        // "" when unavailable
  buildDate: buildDateOnly, // "" when unavailable
  full: getAppVersion(),
});

export default getAppVersion;
